import { Color, Spacing } from '@persona-id/design-tokens';
import { ArrowDownIcon, ArrowUpIcon } from '@persona-id/design-tokens/icons';
import { orderBy } from 'lodash/fp';
import { DateTime } from 'luxon';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import {
  getConditionalRefId,
  getScreenRefId,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryRunTree/InquiryRunTree';
import { useInquiryTemplateTreeContent } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateTree/InquiryTemplateTreeProvider';
import {
  getConditionalPayload,
  getTransitionPayload,
} from 'dashboard/apps/InquiryTemplateEditor/utils/inquiry-run';
import {
  getConditionalDescriptionElement,
  getTransitionEventDescription,
  getTransitionEventsMap,
} from 'dashboard/components/ConnectedInquiryRunTimelineCard/InquiryRunEventTimeline';
import {
  PanZoomBoxContextValue,
  usePanZoomBoxControls,
} from 'dashboard/components-lib/PanZoomBox/PanZoomBox';
import {
  applyScalingOnPoint,
  getTransformationMatrix2DParams,
  TransformationMatrix2D,
} from 'dashboard/lib/utils/cssTransformationMatrix';
import { Event } from 'dashboard/models/event';
import { Inquiry, InquiryEvent } from 'dashboard/models/inquiry';
import { DirectWorkflowRunEvent } from 'dashboard/models/inquiry-run';
import { InquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import Button from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import { Label, Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import menuItemMixins from 'design-system/mixins/menuItem';
import { Elevation, Hairline } from 'design-system/variables/effect';
import { BASE } from 'design-system/variables/spacing';
import { FontWeight } from 'design-system/variables/text';
import { TransitionDuration } from 'design-system/variables/transitions';
import ImageDot from 'images/dashboard/dot.svg';
import { EventName } from 'lib/constants/events';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';
import useDebounced from 'lib/hooks/useDebounced';
import { useDownArrowListener, useEscListener, useUpArrowListener } from 'lib/hooks/useKeyListener';

// timestampToken -> identifies corresponding runStepConfig in ConnectedInquiryRun,
// refToken       -> identifies which InquiryRunTree node ref to pan to
type SelectedEventParams = {
  timestampToken: string;
  refToken: string;
};

/**
 * Generates screen node ref ID to match the correct InquiryRunTree node to pan to,
 * needed primarily because of our usage of connectors, which are duplicate references to the same screen.
 *
 * The logic matches how ref IDs are generated in InquiryRunTree:
 * 1. If there are no prior conditionals, use the step_name (this means the step must be the original instance)
 * 2. Else
 *    2a. If step_name exists in prior steps, use step_name as well
 *        - this means connectors in the run flow will always pan to the original screen
 *          instance if it has already been visited rather than the connector node itself
 *    2b. Otherwise use {step_name}.{parent_workflow_node_token}
 */
const getScreenNodeRefId = (token: string, events: Event[], selectedEventIndex: number): string => {
  const priorEvents = events.slice(0, selectedEventIndex);
  const priorConditional = orderBy(
    (event) => event.attributes['created-at'],
    'desc',
    priorEvents,
  ).find((e) => e.attributes.name === EventName.InquiryRunConditionalRouted);
  if (priorConditional == null) {
    return token;
  } else {
    const priorStepTokens = priorEvents
      .filter((e) => e.attributes.name === EventName.InquiryTransitioned)
      .map((e) => getTransitionPayload(e.attributes.payload.data as InquiryEvent).nextStepName);
    if (priorStepTokens.includes(token)) {
      return token;
    }
    const { treeNodeToken } = getConditionalPayload(
      priorConditional.attributes.payload.data as DirectWorkflowRunEvent,
    );
    return getScreenRefId(token, treeNodeToken);
  }
};

type CenterSelectedScreenParams = {
  desiredScaleLevel?: number;
  selectedScreenRef: HTMLDivElement;
} & Pick<
  PanZoomBoxContextValue,
  | 'zoomableRef'
  | 'scrollRef'
  | 'setTransformationMatrix'
  | 'transformationMatrixRef'
  | 'setTransitionCallback'
  | 'applyScale'
>;

// duplicate of the same method in InquiryTemplateTreeNavigation
// keeping separate for now while changes in both components are ongoing
const centerSelectedScreen = ({
  selectedScreenRef,
  zoomableRef,
  scrollRef,
  desiredScaleLevel = 0.4,
  setTransformationMatrix,
  transformationMatrixRef,
  setTransitionCallback,
  applyScale,
}: CenterSelectedScreenParams) => {
  const selectedScreenRect = selectedScreenRef.getBoundingClientRect();
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const zoomableRect = zoomableRef.current.getBoundingClientRect();
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const scrollRect = scrollRef.current.getBoundingClientRect();

  // our starting midpoints
  const originX = (scrollRect.right + scrollRect.left) / 2 - zoomableRect.left;
  const originY = (scrollRect.top + scrollRect.bottom) / 2 - zoomableRect.top;

  // grab current transformation values
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const transformationMatrix = getTransformationMatrix2DParams(transformationMatrixRef.current);
  const currentScale = transformationMatrix.scaleX;
  const originalX = transformationMatrix.translateX;
  const originalY = transformationMatrix.translateY;

  // calculate the translation amount needed to center on the desired x
  const currentXPos = selectedScreenRect.left - scrollRect.left;
  const targetXPos =
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    (scrollRef.current.clientWidth - selectedScreenRef.clientWidth * currentScale) / 2;

  const translateX = targetXPos - currentXPos;

  // calculate the translation amount needed to center on the desired y
  const currentYPos = selectedScreenRect.top - scrollRect.top;
  const targetYPos =
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    (scrollRef.current.clientHeight - selectedScreenRef.clientHeight * currentScale) / 2;

  const translateY = targetYPos - currentYPos;

  // Apply scaling on new midpoint
  const newMatrix = applyScalingOnPoint(
    [
      [currentScale, 0, translateX + originalX],
      [0, currentScale, translateY + originalY],
      [0, 0, 1],
    ],
    desiredScaleLevel / currentScale,
    originX - translateX, // new x-center after translation
    originY - translateY, // new y-center after translation
  );

  setTransitionCallback(() => () => {
    setTransformationMatrix(newMatrix);
    applyScale();
  });
};

const Container = styled.nav`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.color.background.primary};
  width: 100%;
  height: 100%;
  cursor: default;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex: none;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing[4]} ${Spacing[5]};
  border-top: 1px solid ${({ theme }) => theme.color.background.secondary};
  box-shadow: ${Hairline.Bottom};
  margin-bottom: ${Spacing[2]};
`;

const ResetButton = styled(Button).attrs(() => ({ variant: 'basic', width: 'auto' }))`
  color: ${({ theme }) => theme.color.text.primary};
  opacity: 1;
  transition: opacity ${TransitionDuration.Fast};
  &[aria-hidden='true'] {
    opacity: 0;
  }
`;

const EventsContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: auto;
  overflow-y: auto;
  overflow-x: hidden;
`;

const EventsEmptyDisplay = styled(Paragraph).attrs(() => ({ size: 'md', color: 'disabled' }))`
  margin: ${Spacing[4]} auto;
`;

const FooterContainer = styled.div`
  position: sticky;
  bottom: 0px;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  height: ${Spacing[9]};
  padding: ${Spacing[2]};
  background-color: ${({ theme }) => theme.color.background.primary};
  border-top: 1px solid ${({ theme }) => theme.color.border.primary};
  border-bottom-right-radius: inherit;
`;

const TooltipContent = styled.div`
  display: flex;
  padding: ${Spacing[1]};
  white-space: nowrap;
  overflow: hidden;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${BASE * 2.5}px;
  height: ${BASE * 2.5}px;
  margin-left: ${Spacing[2]};
  border: 1px solid ${({ theme }) => theme.color.text.primary};
  border-radius: ${({ theme }) => theme.element.borderRadius};
`;

const ArrowButton = styled(Button).attrs(() => ({
  width: 'icon',
  variant: 'basic',
}))`
  box-shadow: ${Elevation.High};
`;

interface InquiryRunTreeNavigationProps {
  inquiry: Inquiry;
  events: Event[];
  inquiryTemplateVersion: InquiryTemplateVersion;
  transformationMatrix: TransformationMatrix2D;
  onClearStepthrough: () => void;
  onRowSelected: (timestampToken: string) => void;
}

/**
 * Duplicate of InquiryTemplateTreeNavigation repurposed to facilitate navigation
 * while visualizing Inquiry runs from the Inquiry Details page.
 *
 * Renders Inquiry run event rows which pan and zoom to the corresponding tree node
 * in a step-through experience.
 */
const InquiryRunTreeNavigation = ({
  inquiry,
  events,
  inquiryTemplateVersion,
  transformationMatrix,
  onClearStepthrough,
  onRowSelected,
}: InquiryRunTreeNavigationProps) => {
  const {
    zoomableRef,
    scrollRef,
    setTransformationMatrix,
    transformationMatrixRef,
    setTransitionCallback,
    applyScale,
  } = usePanZoomBoxControls();
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const { screenAndNodeRefs } = useInquiryTemplateTreeContent();
  const [debouncedCenterScreen] = useDebounced(centerSelectedScreen, 200);
  const [selectedEventIndex, setSelectedEventIndex] = useState<number | null>(null);
  const [initialMatrix, setInitialMatrix] = useState<TransformationMatrix2D | undefined>(undefined);

  // save initial load matrix to reset to overview from step-through
  useEffect(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    () => initialMatrix == null && setInitialMatrix(transformationMatrix),
    [initialMatrix, transformationMatrix],
  );

  // reset event index, runStepConfig index (for tree), and initial transform matrix
  const handleClearStepthrough = useCallback(() => {
    setSelectedEventIndex(null);
    onClearStepthrough();
    setTransitionCallback(() => () => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setTransformationMatrix(initialMatrix);
      applyScale();
    });
  }, [
    applyScale,
    initialMatrix,
    onClearStepthrough,
    setTransitionCallback,
    setTransformationMatrix,
  ]);

  /**
   * There are 2 main refToken scenarios:
   * 1. step/connector: {step_name} or {step_name}.{parent_workflow_node_token}
   *    - depending on position within tree + (for connectors) whether the step has
   *      already been visited, the refToken may include the token of the conditional node
   *      immediately preceding the given step (mirrors refId logic in InquiryRunTree)
   * 2. conditional: {node_token}.{index}
   *    - pans directly to the conditional node itself rather than the conditional root node
   *
   * setSelectedEventIndex() mainly updates the visual state of the eventRows,
   * onRowSelected() sets the index of the last runStepTreeConfig to be included
   * in ConnectedInquiryRun, which controls the progress of the run tree path highlight
   */
  const handleUpdateSelectedEvent = useCallback(
    (idx: number, selectedParams: SelectedEventParams) => {
      const { refToken, timestampToken } = selectedParams;
      const treeRef =
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        screenAndNodeRefs.find(({ id }) => id === refToken) ??
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        screenAndNodeRefs.find(({ step }) => step.token === refToken);
      if (treeRef) {
        const { ref: selectedScreenRef } = treeRef;
        setSelectedEventIndex(idx);
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        document
          .getElementById(`${idx}__inquiry-run-event-row`)
          .scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        onRowSelected(timestampToken);
        debouncedCenterScreen({
          scrollRef,
          selectedScreenRef,
          zoomableRef,
          setTransformationMatrix,
          transformationMatrixRef,
          setTransitionCallback,
          applyScale,
        });
      }
    },
    [
      onRowSelected,
      zoomableRef,
      scrollRef,
      screenAndNodeRefs,
      debouncedCenterScreen,
      setTransformationMatrix,
      transformationMatrixRef,
      applyScale,
      setTransitionCallback,
    ],
  );

  // helper for returning selected event params from provided index
  const getSelectedEventValues = useCallback(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    (index: number): SelectedEventParams => {
      const selectedEvent = events[index];
      const { 'created-at': createdAt, name, payload } = selectedEvent.attributes;
      if (name === EventName.InquiryTransitioned) {
        const { nextStepName } = getTransitionPayload(payload.data as InquiryEvent);
        return {
          refToken: getScreenNodeRefId(nextStepName, events, index),
          timestampToken: `${nextStepName}.${createdAt}`,
        };
      } else if (name === EventName.InquiryRunConditionalRouted) {
        const { routeIndex, treeNodeToken } = getConditionalPayload(
          payload.data as DirectWorkflowRunEvent,
        );
        return {
          refToken: getConditionalRefId(treeNodeToken, routeIndex - 1),
          timestampToken: `${treeNodeToken}.${createdAt}`,
        };
      }
    },
    [events],
  );

  // both key press handlers 'wrap around,' with the summary view as the bridge between first/last event indexes
  const handleUpArrow = useCallback(() => {
    if (selectedEventIndex === 0) {
      handleClearStepthrough();
    } else {
      const newIndex = selectedEventIndex != null ? selectedEventIndex - 1 : events.length - 1;
      handleUpdateSelectedEvent(newIndex, getSelectedEventValues(newIndex));
    }
  }, [
    events,
    getSelectedEventValues,
    handleClearStepthrough,
    handleUpdateSelectedEvent,
    selectedEventIndex,
  ]);

  const handleDownArrow = useCallback(() => {
    if (selectedEventIndex === events.length - 1) {
      handleClearStepthrough();
    } else {
      const newIndex = selectedEventIndex != null ? selectedEventIndex + 1 : 0;
      handleUpdateSelectedEvent(newIndex, getSelectedEventValues(newIndex));
    }
  }, [
    events.length,
    getSelectedEventValues,
    handleClearStepthrough,
    handleUpdateSelectedEvent,
    selectedEventIndex,
  ]);

  // key press shortcuts
  useEscListener(handleClearStepthrough);
  useUpArrowListener(handleUpArrow);
  useDownArrowListener(handleDownArrow);

  const handleRowSelected = useCallback(
    (idx: number, selectedParams: SelectedEventParams) =>
      handleUpdateSelectedEvent(idx, selectedParams),
    [handleUpdateSelectedEvent],
  );

  const eventRows = useMemo(() => {
    if (events == null) return [];

    const transitionEventsMap = getTransitionEventsMap(events);

    return events
      .map((event, i) => {
        const { 'created-at': createdAt, name } = event.attributes;
        const occurredAt = DateTime.fromISO(createdAt).toFormat(DATE_AND_TIME_FORMAT);
        const isInPath = selectedEventIndex != null && selectedEventIndex > i;
        let title = '';
        let description: ReactNode = null;
        switch (name) {
          case EventName.InquiryTransitioned: {
            title = 'Step changed';
            description = getTransitionEventDescription({
              inquiry,
              event,
              previousEvent: transitionEventsMap[event.id],
              inquiryTemplateVersion,
              stepsPreview: [],
              withPrefix: false,
            });
            break;
          }
          case EventName.InquiryRunConditionalRouted: {
            title = 'Conditional';
            description = getConditionalDescriptionElement({ event, inquiryTemplateVersion });
            break;
          }
          default:
            return null;
        }
        return (
          <InquiryRunEventRow
            key={i}
            id={`${i}`}
            title={title}
            description={description}
            occurredAt={occurredAt}
            isInPath={isInPath}
            isFirst={i === 0}
            isLast={i === events.length - 1}
            isSelected={i === selectedEventIndex}
            onRowSelected={() => handleRowSelected(i, getSelectedEventValues(i))}
          />
        );
      })
      .filter(Boolean);
  }, [
    events,
    getSelectedEventValues,
    handleRowSelected,
    inquiryTemplateVersion,
    selectedEventIndex,
  ]);
  const disableResetButton = selectedEventIndex == null;
  return (
    <Container>
      <HeaderContainer>
        <Label size='lg' weight='bold'>
          Events
        </Label>
        <ResetButton
          id='inquiry-run__reset-button'
          disabled={disableResetButton}
          aria-disabled={disableResetButton}
          aria-hidden={disableResetButton}
          onClick={handleClearStepthrough}
        >
          Return to summary
        </ResetButton>
      </HeaderContainer>
      {eventRows.length > 0 ? (
        <>
          <EventsContainer>{eventRows}</EventsContainer>
          <FooterContainer>
            <HStack spacing={Spacing[2]}>
              <RunsArrowButton
                id='inquiry-run__prev-event-button'
                tooltipContent={
                  <TooltipContent>
                    <span>Prev event</span>
                    <IconWrapper>
                      <ArrowUpIcon />
                    </IconWrapper>
                  </TooltipContent>
                }
                onClick={handleUpArrow}
              >
                <ArrowUpIcon />
              </RunsArrowButton>
              <RunsArrowButton
                id='inquiry-run__next-event-button'
                tooltipContent={
                  <TooltipContent>
                    <span>Next event</span>
                    <IconWrapper>
                      <ArrowDownIcon />
                    </IconWrapper>
                  </TooltipContent>
                }
                onClick={handleDownArrow}
              >
                <ArrowDownIcon />
              </RunsArrowButton>
            </HStack>
          </FooterContainer>
        </>
      ) : (
        <EventsEmptyDisplay>No events found for this Inquiry run</EventsEmptyDisplay>
      )}
    </Container>
  );
};

interface RunsArrowButtonProps {
  id: string;
  children: ReactNode;
  tooltipContent: ReactNode;
  onClick: () => void;
}

const RunsArrowButton = ({ id, children, tooltipContent, onClick }: RunsArrowButtonProps) => {
  return (
    <Tooltip
      portal={false}
      side='top'
      trigger={
        <ArrowButton id={id} onClick={onClick}>
          {children}
        </ArrowButton>
      }
    >
      {tooltipContent}
    </Tooltip>
  );
};

const ICON_HEIGHT = 10;
const TOP_LINE_HEIGHT = 18;
const ROW_VERTICAL_PADDING = TOP_LINE_HEIGHT - 6;
const MARGIN_HEIGHT = 2;

const Row = styled.div.attrs(() => ({ variant: 'primary' }))`
  ${menuItemMixins.default};
  display: grid;
  grid-template-columns: 60px 1fr;
  border-radius: 0;

  &:hover {
    ${menuItemMixins.hover};
  }

  &[aria-current='true'] {
    ${menuItemMixins.active};
  }
`;

const PlaceHolderTop = styled.div`
  height: ${TOP_LINE_HEIGHT}px;
`;

const TopLine = styled.div<{ isInPath?: boolean; isSelected: boolean }>`
  border-left: 3px solid ${Color.CoreSlate300};
  border-radius: 1px;
  height: ${TOP_LINE_HEIGHT - MARGIN_HEIGHT}px;
  margin-bottom: ${MARGIN_HEIGHT}px;
  transition: border-color ${TransitionDuration.Medium};
  ${({ isInPath, isSelected, theme }) =>
    (isInPath || isSelected) && `border-color: ${theme.color.primary.brand}`}
`;

const BottomLine = styled.div<{ isInPath?: boolean; isSelected: boolean }>`
  border-left: 3px solid ${Color.CoreSlate300};
  border-radius: 1px;
  height: calc(100% - ${TOP_LINE_HEIGHT}px - ${ICON_HEIGHT}px - ${MARGIN_HEIGHT}px);
  margin-top: ${MARGIN_HEIGHT}px;
  transition: border-color ${TransitionDuration.Medium};
  ${({ isInPath, theme }) => isInPath && `border-color: ${theme.color.primary.brand}`}
`;

const Dot = styled(ImageDot)<{ isInPath?: boolean; isSelected: boolean }>`
  height: ${ICON_HEIGHT}px;
  width: ${ICON_HEIGHT}px;
  color: ${Color.CoreSlate300};

  & > circle {
    stroke: none !important;
  }

  transition: color ${TransitionDuration.Medium};
  ${({ isInPath, isSelected, theme }) =>
    (isInPath || isSelected) && `color: ${theme.color.primary.brand}`}
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${ROW_VERTICAL_PADDING}px 0;
`;

const InnerRow = styled.div`
  display: flex;
  flex-direction: row;
`;

const Title = styled(Paragraph).attrs(() => ({ size: 'md' }))`
  color: ${({ theme }) => theme.color.text.primary};
  font-weight: ${FontWeight.Medium};
  margin-right: ${Spacing[2]};
`;

const TimestampLabel = styled(Title)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 230px;
  color: ${({ theme }) => theme.color.text.tertiary};
`;

const Description = styled(TimestampLabel)`
  font-weight: ${FontWeight.Regular};
  white-space: pre-wrap;
  max-width: 275px;
`;

interface InquiryRunEventRowProps {
  id: string;
  title: string;
  description: ReactNode;
  occurredAt: string;
  isSelected: boolean;
  isInPath: boolean;
  isFirst: boolean;
  isLast: boolean;
  onRowSelected: () => void;
}

const InquiryRunEventRow = ({
  id,
  title,
  description,
  occurredAt,
  isSelected,
  isInPath,
  isFirst,
  isLast,
  onRowSelected,
}: InquiryRunEventRowProps) => {
  return (
    <Row
      role='listitem'
      id={`${id}__inquiry-run-event-row`}
      aria-current={isSelected}
      onClick={onRowSelected}
    >
      <LeftColumn>
        {isFirst ? <PlaceHolderTop /> : <TopLine isInPath={isInPath} isSelected={isSelected} />}
        <Dot isInPath={isInPath} isSelected={isSelected} />
        {!isLast && <BottomLine isInPath={isInPath} isSelected={isSelected} />}
      </LeftColumn>
      <RightColumn>
        <InnerRow>
          <Title>{title}</Title>
          {occurredAt && <TimestampLabel>{occurredAt}</TimestampLabel>}
        </InnerRow>
        <Description>{description}</Description>
      </RightColumn>
    </Row>
  );
};

export default InquiryRunTreeNavigation;
